<template>
  <div class="article">
    <div>
      <div class="product-list">
        <transition name="fade" mode="out-in">
          <div v-if="loading" class="loading-skeleton" key="loading">
            <div class="skeleton-item" v-for="i in 6" :key="i">
              <div class="skeleton-image"></div>
              <div class="skeleton-content">
                <div class="skeleton-title"></div>
                <div class="skeleton-category"></div>
                <div class="skeleton-desc"></div>
                <div class="skeleton-button"></div>
              </div>
            </div>
          </div>
          <div class="none" v-else-if="productDataList.length === 0" key="empty">
            <div class="product-none">
              <img src="@/assets/images/none.png" alt="加载失败"/>
              <p>暂无数据</p>
            </div>
          </div>
          <productList :products="productDataList" v-else key="content"/>
        </transition>
      </div>
      <!-- 分页组件 -->
      <BasePagination
        v-if="total>0"
        :current-page="reqParams.pageIndex"
        :page-size="reqParams.pageSize"
        :total="total"
        @current-change="changePagerFn"
      />
    </div>
  </div>
</template>

<script setup>
import { findProductByCategoryId } from "/composables/api/product";

const props = defineProps({
  categoryId: {
    type: String,
    default: '0'
  }
});

const loading = ref(false);
const total = ref(0);
const productDataList = ref([]);

// 筛选条件
const reqParams = reactive({
  pageIndex: 1,
  pageSize: 6,
});

// 加载产品数据
const loadProductData = () => {
  loading.value = true;
  productDataList.value = []; // 清空数据，避免切换时显示上一次的内容

  // 记录加载开始时间
  const startTime = Date.now();

  findProductByCategoryId(reqParams)
    .then(data => {
      if (data && data.data) {
        // 计算已经过去的时间
        const elapsedTime = Date.now() - startTime;
        // 确保骨架屏至少显示500ms，给用户一个平滑的体验
        const delayTime = Math.max(0, 300 - elapsedTime);

        setTimeout(() => {
          productDataList.value = data.data.records || [];
          total.value = data.data.total || 0;
          loading.value = false;
        }, delayTime);
      } else {
        // 同样确保骨架屏有最小显示时间
        const elapsedTime = Date.now() - startTime;
        const delayTime = Math.max(0, 500 - elapsedTime);

        setTimeout(() => {
          productDataList.value = [];
          total.value = 0;
          loading.value = false;
        }, delayTime);
      }
    })
    .catch(error => {
      console.error('获取产品数据失败:', error);
      const elapsedTime = Date.now() - startTime;
      const delayTime = Math.max(0, 500 - elapsedTime);

      setTimeout(() => {
        productDataList.value = [];
        total.value = 0;
        loading.value = false;
      }, delayTime);
    });
};

// 监听分类ID变化，加载对应数据
watch(() => props.categoryId, (categoryId) => {
  if (categoryId !== undefined && categoryId !== null) {
    // 确保categoryId是字符串类型
    reqParams.categoryId = String(categoryId);
    reqParams.pageIndex = 1;
    loadProductData();
  }
}, { immediate: true });

// 分页切换处理
const changePagerFn = (newPage) => {
  reqParams.pageIndex = newPage;
  loadProductData();
};
</script>

<style scoped lang="less">
.article {
  width: 100%;
  position: relative;
}

.loading {
  height: 300px;
  display: flex;
  justify-content: center;
  align-items: center;
  background: #fff;
  &::after {
    content: '';
    width: 40px;
    height: 40px;
    border: 3px solid #f3f3f3;
    border-top: 3px solid #409EFF;
    border-radius: 50%;
    animation: spin 1s linear infinite;
  }
}

.loading-skeleton {
  background: #fff;
  display: flex;
  flex-wrap: wrap;
  width: 100%;
  position: relative;
  top: 0;
  left: 0;
  right: 0;
  min-height: 500px;

  .skeleton-item {
    width: 50%;
    padding: 20px;
    display: flex;
    height: 210px;
    border: 3px solid #f5f5f5;
    box-sizing: border-box;

    .skeleton-image {
      width: 210px;
      height: 120px;
      background: linear-gradient(90deg, #f0f0f0 25%, #f8f8f8 50%, #f0f0f0 75%);
      background-size: 200% 100%;
      animation: shimmer 1.5s infinite;
    }

    .skeleton-content {
      flex: 1;
      padding: 0 20px;

      .skeleton-title {
        height: 20px;
        margin: 5px 0 10px;
        background: linear-gradient(90deg, #f0f0f0 25%, #f8f8f8 50%, #f0f0f0 75%);
        background-size: 200% 100%;
        animation: shimmer 1.5s infinite;
      }

      .skeleton-category {
        height: 16px;
        width: 120px;
        margin-left: auto;
        background: linear-gradient(90deg, #f0f0f0 25%, #f8f8f8 50%, #f0f0f0 75%);
        background-size: 200% 100%;
        animation: shimmer 1.5s infinite;
        margin-bottom: 10px;
      }

      .skeleton-desc {
        height: 50px;
        background: linear-gradient(90deg, #f0f0f0 25%, #f8f8f8 50%, #f0f0f0 75%);
        background-size: 200% 100%;
        animation: shimmer 1.5s infinite;
        margin-bottom: 10px;
      }

      .skeleton-button {
        height: 16px;
        width: 80px;
        margin-left: auto;
        background: linear-gradient(90deg, #f0f0f0 25%, #f8f8f8 50%, #f0f0f0 75%);
        background-size: 200% 100%;
        animation: shimmer 1.5s infinite;
      }
    }
  }
}

.none, .product-none {
  width: 100%;
  min-height: 500px;
}

.product-none {
  text-align: center;
  padding: 150px 0;
  background: #fff;

  img {
    width: 180px;
  }

  p {
    color: #999999;
    padding: 20px 0;
  }
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

@keyframes shimmer {
  0% { background-position: 200% 0; }
  100% { background-position: -200% 0; }
}

.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.3s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}
</style>
